<template>
  <div class="chat-content-channels">
    <h1 class="page-title-20 emphasize--text mb-2">{{ $t('entities.chat.empty.channels.title') }}</h1>
    <div class="body-text-regular-14 emphasize--text lh-20 mb-6 chat-content-channels__subtitle">
      {{ $t('entities.chat.empty.channels.subtitle') }}
    </div>
    <div class="chat-content-channels__list">
      <chat-empty-content-channel
        :title="$t('entities.chat.empty.channels.sms.title')"
        :description="$t('entities.chat.empty.channels.sms.subtitle')"
        @click="onSmsClick"
      >
        <template v-slot:image>
          <tm-icon
            name="chat"
            color="blue"
            size="xxxLarge"
          />
        </template>
      </chat-empty-content-channel>

      <chat-empty-content-channel
        :to="{ name: UserSettingsRoutes.WhatsAppFlow }"
        :title="$t('entities.chat.empty.channels.whatsapp.title')"
        :description="$t('entities.chat.empty.channels.whatsapp.subtitle')"
      >
        <template v-slot:image>
          <img
            class="chat-content-channels__image"
            :src="whatsAppLogo"
          />
        </template>
      </chat-empty-content-channel>

      <chat-empty-content-channel
        :to="{ name: UserSettingsRoutes.LiveChatIndex }"
        :title="$t('entities.chat.empty.channels.livechat.title')"
        :description="$t('entities.chat.empty.channels.livechat.subtitle')"
      >
        <template v-slot:image>
          <img
            class="chat-content-channels__image"
            :src="liveChatLogo"
          />
        </template>
      </chat-empty-content-channel>

      <chat-empty-content-channel
        :to="{ name: UserSettingsRoutes.FacebookFlow }"
        :title="$t('entities.chat.empty.channels.facebook.title')"
        :description="$t('entities.chat.empty.channels.facebook.subtitle')"
      >
        <template v-slot:image>
          <img
            class="chat-content-channels__image"
            :src="facebookLogo"
          />
        </template>
      </chat-empty-content-channel>

      <chat-empty-content-channel
        :to="{ name: UserSettingsRoutes.InstagramFlow }"
        :title="$t('entities.chat.empty.channels.instagram.title')"
        :description="$t('entities.chat.empty.channels.instagram.subtitle')"
      >
        <template v-slot:image>
          <img
            class="chat-content-channels__image"
            :src="instagramLogo"
          />
        </template>
      </chat-empty-content-channel>
    </div>

    <tm-translate
      class="body-text-regular-14 emphasize--text mt-6"
      keypath="entities.chat.empty.channels.bottomText"
      tag="div"
    >
      <template v-slot:link-text>
        <tm-button
          color="primary"
          size="xMedium"
          text-link
          @click="onSolved"
        >
          <span class="solved-btn">{{ $t('entities.chat.empty.channels.linkText') }}</span>
        </tm-button>
      </template>
    </tm-translate>
  </div>
</template>

<script setup lang="ts">
import TmTranslate from '@/components/TmTranslate.vue'
import whatsAppLogo from '@/assets/images/social/whatsapp.svg'
import facebookLogo from '@/assets/images/social/facebook.svg'
import instagramLogo from '@/assets/images/social/instagram.svg'
import liveChatLogo from '@/assets/images/live-chat.svg'
import { setCookieByName } from '@/utils/cookies'
import TmButton from '@/components/shared/TmButton.vue'
import TmIcon from '@/components/shared/TmIcon.vue'
import { useActiveChat } from '@/composition/domain/chats/chatActive'
import { PromoChatCookieName, PromoChatState } from '@/data/models/domain/chats/types'
import ChatEmptyContentChannel from '@/components/domain/chats/content/contentEmpty/ChatEmptyContentChannel.vue'
import { getRouterService, getUserService } from '@/core/container/ioc'
import { computed } from '@/composition/vue/compositionApi'
import { UserSettingsRoutes } from '@/routes/user/settings'
import { TenDlcTollFreeRoutes } from '@/routes/types'
import type { TmRoutes } from '@/services/route/routerTypes'

const userService = getUserService()

const isUSorPR = computed(() => ['US', 'PR'].includes(userService.getCurrentUserCountryId()))

const onSolved = () => {
  setCookieByName(PromoChatCookieName, PromoChatState.Solved, 400)
  const { isPromoChatSolved } = useActiveChat()
  isPromoChatSolved.value = true
}

const onSmsClick = () => {
  if (isUSorPR.value) {
    const tollFreeRoute: TmRoutes = {
      name: TenDlcTollFreeRoutes.chooseNumber,
      query: {
        country: 'US',
      },
    }
    getRouterService().push(tollFreeRoute)
  } else {
    getRouterService().push({ name: UserSettingsRoutes.SmsVoiceIndex })
  }
}
</script>

<style lang="scss" scoped>
.chat-content-channels {
  width: 100%;
  max-width: 720px;
  text-align: center;
  gap: 24px;
  padding: 40px;
  border-radius: $border-radius-lg;
  border: 1px solid $gray2;
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;

  @include media-md-max {
    padding: 20px;
  }

  &__subtitle {
    padding: 0 90px;

    @include media-md-max {
      padding: 0;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__image {
    width: 24px;
    height: 24px;
  }
}

.solved-btn {
  color: $blue !important;
}
</style>
